import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import ICart from "../../../models/ICart";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetCartDTO from "../../../models/DTO/IOnGetCartDTO";
import IGetCartDTO from "../../../models/DTO/IGetCartDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import OwTitle from "../../shared/OwTitle/OwTitle";
import styled from "styled-components";
import TitleTagEnum from "../../../models/TitleTagEnum";
import { EcommerceCheckoutShipTo } from "../EcommerceCheckoutShipTo/EcommerceCheckoutShipTo";
import IOnUpdateCartShipToDTO from "../../../models/DTO/IOnUpdateCartShipToDTO";
import { EcommerceStripeCardForm } from "../EcommerceStripeCardForm/EcommerceStripeCardForm";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe, StripeElements } from "@stripe/stripe-js";
import ICreateCartPaymentIntentDTO from "../../../models/DTO/ICreateCartPaymentIntentDTO";
import IOnCreateCartPaymentIntentDTO from "../../../models/DTO/IOnCreateCartPaymentIntentDTO";
import ICartValidateInventoryDTO from "../../../models/DTO/ICartValidateInventoryDTO";
import ICartOnValidateInventoryDTO from "../../../models/DTO/ICartOnValidateInventoryDTO";
import SocketService from "../../../services/SocketService";
import ICart_Item from "../../../models/ICart_Item";
import IUpdateCartItemDTO from "../../../models/DTO/IUpdateCartItemDTO";
import IDeleteCartItemDTO from "../../../models/DTO/IDeleteCartItemDTO";
import IOnDeleteCartItemDTO from "../../../models/DTO/IOnDeleteCartItemDTO";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import IUpdatePaymentIntentDTO from "../../../models/DTO/IUpdatePaymentIntentDTO";
import IOnUpdatePaymentIntentDTO from "../../../models/DTO/IOnUpdatePaymentIntentDTO";
import { EcommerceCheckoutCartItem } from "../EcommerceCheckoutCartItem/EcommerceCheckoutCartItem";
import OwButton from "../../shared/OwButton/OwButton";
import ICartCompleteCheckoutDTO from "../../../models/DTO/ICartCompleteCheckoutDTO";
import ICartOnCompleteCheckoutDTO from "../../../models/DTO/ICartOnCompleteCheckoutDTO";
import { useNavigate } from "react-router-dom";
import OwTextArea from "../../shared/OwTextArea/OwTextArea";
import { Socket } from "socket.io-client";
import ICartOnCalculateTotalsDTO from "../../../models/DTO/ICartOnCalculateTotalsDTO";
import ICartCalculateTotalsDTO from "../../../models/DTO/ICartCalculateTotalsDTO";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IUpdateCartPropDTO from "../../../models/DTO/IUpdateCartPropDTO";
import IOnUpdateCartPropDTO from "../../../models/DTO/IOnUpdateCartPropDTO";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";

type Props = {};

export const EcommerceCheckout: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const history = useNavigate();

    const currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const [cart, setCart] = useState<ICart>();

    const [initTotalsCalculated, setInitTotalsCalculated] = useState(false);
    const [stripeLoaded, setStripeLoaded] = useState(false);
    const [stripePromise, setStripePromise] = useState<Stripe | null>(null);
    const [paymentIntent, setPaymentIntent] = useState<IOnCreateCartPaymentIntentDTO>();
    const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
    const [isCartValid, setIsCartValid] = useState<boolean>(false);
    const [placeOrderActive, setPlaceOrderActive] = useState<boolean>(false);
    const [cartUpdating, setCartUpdating] = useState<boolean>(false);
    const [paymentForm, setPaymentForm] = useState<HTMLFormElement | null>(null);
    const [stripe, setStripe] = useState<Stripe | null>();
    const [elements, setElements] = useState<StripeElements | null>();
    const [setupInventoryOn, setSetupInventoryOn] = useState(false);
    const [updatingItemGuid, setUpdatingItemGuid] = useState("");
    const [cartUpdated, setCartUpdated] = useState(false);

    const refreshOnValidateInventory = () => {
        socket.removeListener(EndpointsEnum.CartOnValidateInventory, onValidateInventory);
        socket.on(EndpointsEnum.CartOnValidateInventory, onValidateInventory);
    };

    const refreshOnCompleteCheckout = () => {
        socket.removeListener(EndpointsEnum.CartOnCompleteCheckout, onCompleteCheckout);
        socket.on(EndpointsEnum.CartOnCompleteCheckout, onCompleteCheckout);
    };

    const createPaymentIntent = () => {
        if (!vendorModel.net_term_ecomm_user) {
            const payload: ICreateCartPaymentIntentDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
            };

            socket.emit(EndpointsEnum.CartCreatePaymentIntent, payload);

            setPaymentIntentLoading(true);
        }
    };

    const onCreatePaymentIntent = (data: IOnCreateCartPaymentIntentDTO) => {
        setPaymentIntent(data);
        setPaymentIntentLoading(false);
    };

    const updatePaymentIntent = () => {
        if (!vendorModel.net_term_ecomm_user && paymentIntent) {
            const payload: IUpdatePaymentIntentDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                payment_intent_id: paymentIntent.paymentIntentId,
            };

            socket.emit(EndpointsEnum.CartUpdatePaymentIntent, payload);

            setPaymentIntentLoading(true);
        }
    };

    const onUpdatePaymentIntent = (data: IOnUpdatePaymentIntentDTO) => {
        setPaymentIntent(data);
        setPaymentIntentLoading(false);
    };

    const onCalculateTotals = (data: ICartOnCalculateTotalsDTO) => {
        setInitTotalsCalculated(true);
        setCart(data.cart);
    };

    const notify = () => {
        setPlaceOrderActive(false);
    };

    const onGetCart = (data: IOnGetCartDTO) => {
        setCart(data.cart);
    };

    useEffect(() => {
        if (!initTotalsCalculated && cart) {
            const payload: ICartCalculateTotalsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                cartGuid: cart.guid,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.CartCalculateTotals, payload);
        }

        if (!setupInventoryOn && cart) {
            // refreshOnValidateInventory();
            // refreshOnCompleteCheckout();

            setSetupInventoryOn(true);
        }
    }, [cart]);

    useEffect(() => {
        if (cart && initTotalsCalculated) {
            if (!paymentIntent && cart?.Cart_Items?.length) {
                createPaymentIntent();
            }
        }
    }, [cart, initTotalsCalculated]);

    const onUpdateShipTo = (data: IOnUpdateCartShipToDTO) => {
        getCart();
        setCartUpdated(true);
    };

    useEffect(() => {
        if (cartUpdated) {
            updatePaymentIntent();
            setCartUpdated(false);
        }
    }, [cartUpdated]);

    const onUpdateItem = () => {
        setUpdatingItemGuid("");
        setCartUpdated(true);

        getCart();
    };

    const onDeleteItem = (data: IOnDeleteCartItemDTO) => {
        getCart();
        setCartUpdated(true);
    };

    const onUpdateProp = (data: IOnUpdateCartPropDTO) => {
        const notification: INotification = {
            message: "successfully_updated_cart",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        getCart();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CartOnCreatePaymentIntent, fn: onCreatePaymentIntent },
        { endpoint: EndpointsEnum.CartOnUpdatePaymentIntent, fn: onUpdatePaymentIntent },
        { endpoint: EndpointsEnum.CartOnGetCart, fn: onGetCart },
        { endpoint: EndpointsEnum.CartOnUpdateShipTo, fn: onUpdateShipTo },
        { endpoint: EndpointsEnum.Notify, fn: notify },
        { endpoint: EndpointsEnum.CartOnDeleteItem, fn: onDeleteItem },
        { endpoint: EndpointsEnum.CartOnUpdateItem, fn: onUpdateItem },
        { endpoint: EndpointsEnum.CartOnCalculateTotals, fn: onCalculateTotals },
        { endpoint: EndpointsEnum.CartOnUpdateProp, fn: onUpdateProp },
    ];

    useEffect(() => {
        if (!stripeLoaded) {
            if (supplierModel.Supplier_Integration.stripe_publishable_api_key) {
                loadStripe(supplierModel.Supplier_Integration.stripe_publishable_api_key).then((_stripe) => {
                    setStripePromise(_stripe);
                });
            }

            setStripeLoaded(true);
        }

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getCart();

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }

            const listeners = socket.listeners(EndpointsEnum.CartOnValidateInventory);

            // to be sure we clean up all the listeners that are CartOnValidateInventory and CartOnCompleteCheckout
            // we get into a weird state sometimes, no idea why yet.
            socket.removeAllListeners(EndpointsEnum.CartOnValidateInventory);
            socket.removeAllListeners(EndpointsEnum.CartOnCompleteCheckout);
        };
    }, []);

    useEffect(() => {
        if (!setupInventoryOn && stripe) {
            // refreshOnValidateInventory();
            // refreshOnCompleteCheckout();

            setSetupInventoryOn(true);
        }
    }, [stripe]);

    const getCart = () => {
        const payload: IGetCartDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CartGetCart, payload);
    };

    const validateInventory = () => {
        refreshOnValidateInventory();

        const payload: ICartValidateInventoryDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CartValidateInventory, payload);

        // console.log("validateInventory");

        setPlaceOrderActive(true);
    };

    const onValidateInventory = (data: ICartOnValidateInventoryDTO) => {
        // console.log("onValidateInventory");
        if (vendorModel.net_term_ecomm_user) {
            // console.log("onValidateInventory net_term_ecomm_user");
            refreshOnCompleteCheckout();

            const payload: ICartCompleteCheckoutDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
            };

            if (cart?.note) {
                payload.note = cart.note;
            }

            if (cart?.po_no) {
                payload.po_no = cart.po_no;
            }

            socket.emit(EndpointsEnum.CartCompleteCheckout, payload);
        } else {
            // console.log("onValidateInventory else");
            if (data.cartItems.length) {
                // do something with the data. since there is an inventory issue
                setPlaceOrderActive(false);
            } else {
                // process the order as normal
                // form={"paymentForm"} type={"submit"} // how to manually submit the form data?
                // @ts-expect-error
                const paymentFormElem = document.forms.paymentForm;

                if (paymentFormElem) {
                    handleSubmit();
                }
            }
        }
    };

    const onCompleteCheckout = (data: ICartOnCompleteCheckoutDTO) => {
        // console.log("onCompleteCheckout");
        if (cart) {
            const url = `/ecomm/checkout/complete?g=${cart.guid}`;

            history(url);
        }
    };

    const handleSubmit = async () => {
        // event.preventDefault();

        if (!stripe || !elements || !cart) {
            return;
        }

        // first before submitting we need to validate the inventory in the cart.

        const apiUrl = SocketService.getApiEndpointURl();

        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${apiUrl}/stripe-complete-checkout/${cart.guid}`,
            },
            // redirect: "if_required"
        });

        if (result.error) {
            setPlaceOrderActive(false);
        }
    };

    const setPaymentFormRef = (formRef: HTMLFormElement | null) => {
        setPaymentForm(formRef);
    };

    const setElementsFn = (_elements: StripeElements | null) => {
        setElements(_elements);
    };

    const setStripeFn = (_stripe: Stripe | null) => {
        setStripe(_stripe);
    };

    const updateAddlInput = (cartItem: ICart_Item, addlInput: string) => {
        const payload: IUpdateCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            addlInput,
            cartItemGuid: cartItem.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CartUpdateItem, payload);

        setCartUpdating(true);
        setUpdatingItemGuid(cartItem.guid);
    };

    const updateQty = (cartItem: ICart_Item, qty: number) => {
        if (cartItem.qty !== qty) {
            const payload: IUpdateCartItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                qty,
                cartItemGuid: cartItem.guid,
                uomSetId: cartItem.uom_set_id,
                recalculateTotals: true,
            };

            if (!userModel.isSupplierUser) {
                payload.vendorGuid = vendorModel.guid;
            }

            socket.emit(EndpointsEnum.CartUpdateItem, payload);

            setCartUpdating(true);
            setUpdatingItemGuid(cartItem.guid);
        }
    };

    const deleteItem = (cartItem: ICart_Item) => {
        const payload: IDeleteCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            cartItemGuid: cartItem.guid,
            recalculateTotals: true,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        socket.emit(EndpointsEnum.CartDeleteItem, payload);

        setCartUpdating(true);
    };

    const updateNote = (e: ChangeEvent<any>) => {
        if (cart) {
            const updatedCart = {
                ...cart,
            };

            updatedCart.note = e.target.value;

            setCart(updatedCart);
        }
    };

    const updatePo = (e: ChangeEvent<any>) => {
        if (cart) {
            const updatedCart = {
                ...cart,
            };

            updatedCart.po_no = e.target.value;

            setCart(updatedCart);
        }
    };

    const saveCartMetaData = async () => {
        if (cart) {
            const payload: IUpdateCartPropDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
                po_no: cart.po_no,
                note: cart.note,
            };

            socket.emit(EndpointsEnum.CartUpdateProp, payload);
        }
    };

    return (
        <ECommerceCheckoutContainerDiv className={"container-fluid container-md"}>
            <OwTitle title={"checkout"} />

            <ECommerceInsideContainer className={"d-flex flex-column flex-md-row flex-grow-1 flex-md-grow-0 row"}>
                <ECommerceMainDetailsDiv className={"col-md-8 col-lg-6 col-xl-6"}>
                    <ECommerceShippingAddressContainerDiv className={"flex-column"}>
                        <div className="">
                            <OwTitle title={"1_shipping_address"} tag={TitleTagEnum.H5} />
                        </div>
                        <div className="w-100">
                            {cart ? (
                                <EcommerceCheckoutShipTo
                                    cart={cart}
                                    socket={socket}
                                    notifyFn={uiContext.notifyFn}
                                    setActivityIndicator={uiContext.setActivityIndicator}
                                />
                            ) : null}
                        </div>
                    </ECommerceShippingAddressContainerDiv>

                    {vendorModel.net_term_ecomm_user ? null : (
                        <ECommercePaymentMethodContainerDiv>
                            <div className="">
                                <OwTitle title={"2_payment_method"} tag={TitleTagEnum.H5} />
                            </div>
                            <div className="w-100">
                                {paymentIntent && cart ? (
                                    <Elements stripe={stripePromise} options={paymentIntent as any}>
                                        <EcommerceStripeCardForm
                                            socket={socket}
                                            cart={cart}
                                            isCartValid={isCartValid}
                                            setPaymentFormRef={setPaymentFormRef}
                                            setElements={setElementsFn}
                                            setStripe={setStripeFn}
                                        />
                                    </Elements>
                                ) : (
                                    <div>
                                        <ActivityIndicator active={true} /> <OwLabel title={"loading"} />
                                    </div>
                                )}
                            </div>
                        </ECommercePaymentMethodContainerDiv>
                    )}

                    <ECommerceReviewItemsContainerDiv className={"flex-column"}>
                        <div className="">
                            {vendorModel.net_term_ecomm_user ? (
                                <OwTitle title={"2_review_items"} tag={TitleTagEnum.H5} />
                            ) : (
                                <OwTitle title={"3_review_items"} tag={TitleTagEnum.H5} />
                            )}
                        </div>
                        <div className=" flex-grow-1 flex-column">
                            {cart?.Cart_Items?.filter((cartItem) => !cartItem.main_kit_cart_item_id).map((cartItem) => {
                                let kitBuilderItems: ICart_Item[] | undefined = [];

                                if (cartItem.is_kit_builder) {
                                    kitBuilderItems = cart?.Cart_Items?.filter((kitCartItem) => {
                                        return kitCartItem.main_kit_cart_item_id === cartItem.id;
                                    });
                                }

                                return (
                                    <EcommerceCheckoutCartItem
                                        key={cartItem.guid}
                                        cartItem={cartItem}
                                        priceGroup={vendorModel.Price_Group}
                                        updateQty={updateQty}
                                        deleteItem={deleteItem}
                                        isUpdating={cartItem.guid === updatingItemGuid}
                                        updateAddlInput={updateAddlInput}
                                        kitBuilderItems={kitBuilderItems}
                                    />
                                );
                            })}
                        </div>
                    </ECommerceReviewItemsContainerDiv>
                </ECommerceMainDetailsDiv>
                <EcommerceRightSideContainer className={"col-md-4 col-lg-3 flex-grow-1 flex-md-grow-0 flex-column-reverse flex-sm-column"}>
                    <ECommerceFloatingSummaryDiv className={"mb-2 mb-md-0"}>
                        <div>
                            <OwButton
                                title={"place_your_order"}
                                className={"btn-warning"}
                                fn={validateInventory}
                                active={placeOrderActive}
                                disableOnActive={true}
                                activeTitle={"please_wait"}
                                disabled={
                                    (!paymentIntent && !vendorModel.net_term_ecomm_user) ||
                                    !cart?.Cart_Items?.length ||
                                    cartUpdated ||
                                    paymentIntentLoading
                                }
                            />
                        </div>
                        <div>
                            <hr />
                        </div>
                        <div>
                            <div>
                                <OwTitle title={"order_summary"} tag={TitleTagEnum.H5} />
                            </div>
                            <div className={"items"}>
                                <OwLabel title={"items"} /> {currencyFormatter.format(cart?.sub_total || 0)}
                            </div>

                            {vendorModel.tax_exempt ? null : (
                                <div className={"tax"}>
                                    <OwLabel title={"tax"} /> {currencyFormatter.format(cart?.tax || 0)}
                                </div>
                            )}

                            <div className={"shipping"}>
                                <OwLabel title={"shipping"} /> {currencyFormatter.format(cart?.shipping || 0)}
                            </div>

                            <br />
                            <div className={"total"}>
                                <OwLabel title={"order_total"} /> {currencyFormatter.format(cart?.grand_total || cart?.sub_total || 0)}
                            </div>
                        </div>
                    </ECommerceFloatingSummaryDiv>
                    <EcommerceNoteDiv>
                        <div>
                            <OwLabel title={"po_no"} isBold={true} />
                        </div>
                        <div>
                            <OwTextArea name={"po_no"} fn={updatePo} value={cart?.po_no || ""} />
                        </div>
                    </EcommerceNoteDiv>
                    <EcommerceNoteDiv>
                        <div>
                            <OwLabel title={"note"} isBold={true} />
                        </div>
                        <div>
                            <OwTextArea name={"note"} fn={updateNote} value={cart?.note || ""} />
                        </div>
                    </EcommerceNoteDiv>
                    {vendorModel.net_term_ecomm_user ? null : (
                        <div className={"mt-2"}>
                            <OwButton title={"save"} fn={saveCartMetaData} />
                        </div>
                    )}
                </EcommerceRightSideContainer>
            </ECommerceInsideContainer>
        </ECommerceCheckoutContainerDiv>
    );
};

const ECommerceCheckoutContainerDiv = styled.div``;

const ECommerceInsideContainer = styled.div`
    display: flex;
`;

const ECommerceShippingAddressContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const ECommercePaymentMethodContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const ECommerceReviewItemsContainerDiv = styled.div`
    margin-top: 2rem;
    display: flex;

    h5 {
        margin-top: 0 !important;
        padding-top: 0;
    }
`;

const EcommerceRightSideContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ECommerceFloatingSummaryDiv = styled.div`
    //min-width: 13rem;
    border: 1px solid var(--main-text-color);
    border-radius: 3px;
    padding: 0.5rem;
    height: fit-content;

    button {
        width: 100%;
    }

    hr {
        color: var(--main-text-color);
        border-color: var(--main-text-color);
    }

    .items,
    .tax,
    .shipping,
    .total {
        display: flex;
        justify-content: space-between;
    }
`;

const ECommerceMainDetailsDiv = styled.div``;

const EcommerceNoteDiv = styled.div`
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    height: auto;
`;
